import { computed, Injectable, signal } from '@angular/core';
import { CookieCategory, CookieConsent, DEFAULT_CONSENT } from '../models/cookie-consent.model';

const STORAGE_KEY = 'cookie_consent_v1';
const BANNER_DISMISSED_KEY = 'cookie_banner_dismissed';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  public readonly categories: readonly CookieCategory[] = ['necessary', 'media', 'social', 'analytics'];

  private readonly _consent = signal<CookieConsent>(this.load());

  public readonly consent = computed(() => this._consent());
  public readonly hasConsent = computed(() => !!this._consent().givenAt);

  private readonly _bannerDismissed = signal<boolean>(sessionStorage.getItem(BANNER_DISMISSED_KEY) === '1');
  public readonly shouldShowBanner = computed(() => !this.hasConsent() && !this._bannerDismissed());

  public dismissBannerTemporarily() {
    this._bannerDismissed.set(true);
    sessionStorage.setItem(BANNER_DISMISSED_KEY, '1');
  }
  public resetBannerDismiss() {
    this._bannerDismissed.set(false);
    sessionStorage.removeItem(BANNER_DISMISSED_KEY);
  }

  private load(): CookieConsent {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(DEFAULT_CONSENT);
      const parsed = JSON.parse(raw) as CookieConsent;
      if (parsed.version !== DEFAULT_CONSENT.version) return structuredClone(DEFAULT_CONSENT);
      return { ...DEFAULT_CONSENT, ...parsed, choices: { ...DEFAULT_CONSENT.choices, ...parsed.choices } };
    } catch {
      return structuredClone(DEFAULT_CONSENT);
    }
  }

  public setAll(accept: boolean) {
    const next: CookieConsent = {
      version: DEFAULT_CONSENT.version,
      givenAt: new Date().toISOString(),
      choices: { necessary: true, media: accept, social: accept, analytics: accept },
    };
    this.persist(next);
  }

  public setChoices(partial: Partial<Record<CookieCategory, boolean>>) {
    const current = this._consent();
    const next: CookieConsent = {
      ...current,
      givenAt: new Date().toISOString(),
      choices: { ...current.choices, ...partial, necessary: true },
    };
    this.persist(next);
  }

  public consentFor(category: CookieCategory): boolean {
    return !!this._consent().choices[category];
  }

  public reset() {
    this.persist({ ...structuredClone(DEFAULT_CONSENT), givenAt: undefined });
    localStorage.removeItem(STORAGE_KEY);
    this.resetBannerDismiss();
  }

  private persist(value: CookieConsent) {
    this._consent.set(value);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    this.resetBannerDismiss();
  }
}
